/**
 * A saved filter: the `{ logic, conditions }` a dynamic list keeps in
 * `data.filter` and a link field keeps as its `filter`. Postgres runs it
 * (`filter_records(record_type, filter)`); this module only names its shape.
 */

// Type aliases, not interfaces: a filter goes out as a jsonb variable, typed
// Record<string, unknown>, which only an alias's implicit index signature meets.

/** One condition. `enabled: false` keeps it in the filter without applying it. */
export type SavedFilterCondition = {
  id?: string
  field: string
  operator: string
  value?: unknown
  enabled?: boolean
}

export type SavedFilter = {
  /** How conditions combine; absent means "and". */
  logic?: "and" | "or"
  conditions: SavedFilterCondition[]
}

/** Whether a filter has a condition that applies: a filter without one
 *  matches nothing in Postgres, so callers treat it as no filter at all. */
export function hasActiveConditions(filter: SavedFilter | null | undefined): filter is SavedFilter {
  return !!filter?.conditions?.some((c) => c.enabled !== false)
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

/** Why `value` is not a saved filter, or null when it is one. */
export function savedFilterProblem(value: unknown): string | null {
  if (!isObject(value)) return `must be an object, not ${JSON.stringify(value)}`
  if (value.logic !== undefined && value.logic !== "and" && value.logic !== "or") {
    return `"logic" must be "and" or "or", not ${JSON.stringify(value.logic)}`
  }
  if (!Array.isArray(value.conditions)) return `"conditions" must be an array`
  for (const [i, c] of value.conditions.entries()) {
    if (!isObject(c) || typeof c.field !== "string" || typeof c.operator !== "string") {
      return `condition ${i + 1} needs a "field" and an "operator"`
    }
  }
  return null
}
